import { useMemo, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router-dom';
import { Casca } from './layout/Casca';
import { Tela } from './telas/Tela';
import { Login } from './telas/Login';
import { Carregando } from './componentes/Estado';
import { useSessao } from './auth/sessao';

// `/p/:slug/:aba` — a chave do documento entra como `?key=` na aba doc, porque
// unit_key tem barra (`src/app/dispatch.service.ts`) e sujaria o caminho.
//
// A tabela sai como constante, e não como roteador pronto, para o teste poder
// montá-la num roteador de memória e escolher a URL inicial. Roteador de
// navegador criado no escopo do módulo é um só para toda a suíte.
export const ROTAS: RouteObject[] = [
  { path: '/', element: <Navigate to="/p" replace /> },
  // sem projeto na URL a casca escolhe o primeiro a que o usuário tem acesso —
  // qual é o "primeiro" só se sabe depois de /api/projects responder
  { path: '/p', element: <Casca /> },
  {
    path: '/p/:slug',
    element: <Casca />,
    children: [
      // sem aba, a casca completa a URL com Overview; a rota precisa casar
      // primeiro, senão a URL curta nem chega nela
      { index: true, element: null },
      // uma rota com `:aba`, e não treze com o nome de cada uma: quem diz se a
      // aba existe é o catálogo, dentro da casca, que também sabe se o usuário
      // é admin. Treze rotas literais deixariam `/p/gw/users` casar para o dev.
      { path: ':aba', element: <Tela /> },
    ],
  },
  // rota desconhecida não é erro: é link de versão anterior do painel, ou aba
  // que deixou de existir. Volta para /p, que sabe se recompor.
  { path: '*', element: <Navigate to="/p" replace /> },
];

// O basename sai do `base` do Vite, e não de uma constante daqui: o dia em que o
// painel precisar ser servido sob um prefixo (um proxy de caminho, uma prévia
// lado a lado), muda-se o `base` do build e o roteador acompanha.
const BASE = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

/**
 * O que se vê antes de haver painel: nada, o login, ou o painel.
 *
 * Separado do roteador porque a sessão é anterior a qualquer rota — e porque
 * assim o teste monta este portão sobre um roteador de memória, sem o roteador
 * de navegador, que é único por módulo.
 */
export function Portao({ children }: { children: ReactNode }) {
  const { estado } = useSessao();
  if (estado === 'carregando') {
    return <div style={{ padding: 30 }}><Carregando rotulo="sua sessão" /></div>;
  }
  if (estado === 'fora') return <Login />;
  return <>{children}</>;
}

export function Aplicacao() {
  // criado uma vez: refazer o roteador a cada render joga o usuário de volta
  // para a URL inicial em toda mudança de estado da sessão
  const roteador = useMemo(() => createBrowserRouter(ROTAS, { basename: BASE }), []);
  return <Portao><RouterProvider router={roteador} /></Portao>;
}
